// Drop-in language picker. Used by:
//   * apps/sell ProfilePage settings section
//   * apps/shop SettingsLanguagePage
//   * any header user menu that wants a quick switcher
//
// Writes to core.users.preferred_locale via @pc/db setPreferredLocale and
// immediately calls setLanguage() so the UI flips without a reload.

import { useCallback, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { setPreferredLocale } from '@pc/db';

import { setLanguage, currentLocale } from '../i18n';
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  normalizeLocale,
  type SupportedLocale,
} from '../locales';

export interface LanguagePickerProps {
  /**
   * Optional list filter — pass e.g. the settings.supported_locales from
   * core.translation_settings to render only enabled locales. When omitted,
   * shows all 15.
   */
  enabledLocales?: readonly string[];
  /** Hide the descriptive help text (when used in a compact header menu). */
  compact?: boolean;
  /** Persist the choice to core.users.preferred_locale. Default true. */
  persist?: boolean;
  /** Called after a successful switch (e.g. to show a toast). */
  onChange?: (locale: SupportedLocale) => void;
  /** Inline className passthrough. */
  className?: string;
  id?: string;
}

export function LanguagePicker(props: LanguagePickerProps): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState<SupportedLocale>(() =>
    normalizeLocale(currentLocale()),
  );
  const [busy, setBusy] = useState(false);

  const enabledSet = new Set(
    (props.enabledLocales && props.enabledLocales.length > 0
      ? props.enabledLocales
      : (SUPPORTED_LOCALES as readonly string[])
    ).map(normalizeLocale),
  );

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLSelectElement>) => {
      const next = normalizeLocale(e.target.value);
      setValue(next);
      setBusy(true);
      try {
        await setLanguage(next);
        if (props.persist !== false) {
          // best-effort — if not authenticated, this throws and we swallow.
          // Persist the locale VERBATIM (including 'en'): storing null for
          // English made an explicit English choice indistinguishable from
          // "never chose", so it could not propagate to other devices.
          try {
            await setPreferredLocale(next);
          } catch {
            // not signed in → just keep the localStorage choice
          }
        }
        props.onChange?.(next);
      } finally {
        setBusy(false);
      }
    },
    [props.onChange, props.persist],
  );

  return (
    <div className={props.className}>
      {!props.compact && (
        <label
          htmlFor={props.id ?? 'pc-language-picker'}
          className="block text-sm font-medium"
        >
          {t('app.languagePicker.label', 'Display language')}
        </label>
      )}
      <select
        id={props.id ?? 'pc-language-picker'}
        value={value}
        onChange={handleChange}
        disabled={busy}
        className="mt-1 block w-full rounded border border-line bg-bg px-3 py-2 text-sm"
      >
        {(SUPPORTED_LOCALES as readonly SupportedLocale[]).map((loc) => {
          if (!enabledSet.has(loc)) return null;
          return (
            <option key={loc} value={loc}>
              {LOCALE_LABELS[loc]}
            </option>
          );
        })}
      </select>
      {!props.compact && (
        <p className="mt-2 text-xs text-muted">
          {t(
            'app.languagePicker.help',
            'All buttons, menus, and pages appear in this language. Messages from other people are translated automatically.',
          )}
        </p>
      )}
    </div>
  );
}

/**
 * Footer-tuned language picker — single-line `<label> <select>` styled to
 * match the small-text footers in apps/{sell,shop,web} and the public
 * storefront pages. Persist defaults to true so signed-in users get their
 * preference written to `core.users.preferred_locale`; signed-out users
 * fall through to localStorage. Use this in EVERY user-visible footer so
 * logged-out users (sign-in pages, public storefronts, public product
 * pages) can switch languages before authenticating. See
 * ai-rules/i18n-no-hardcoded.md.
 */
export function FooterLanguagePicker(props: {
  enabledLocales?: readonly string[];
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState<SupportedLocale>(() =>
    normalizeLocale(currentLocale()),
  );
  const [busy, setBusy] = useState(false);

  const enabledSet = new Set(
    (props.enabledLocales && props.enabledLocales.length > 0
      ? props.enabledLocales
      : (SUPPORTED_LOCALES as readonly string[])
    ).map(normalizeLocale),
  );

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLSelectElement>) => {
      const next = normalizeLocale(e.target.value);
      setValue(next);
      setBusy(true);
      try {
        await setLanguage(next);
        try {
          // Verbatim ('en' included) — see LanguagePicker above.
          await setPreferredLocale(next);
        } catch {
          // not signed in → localStorage choice already persisted by
          // i18next-browser-languagedetector via setLanguage above.
        }
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const labelText = t('app.languagePicker.label', 'Display language');

  return (
    <span className={`inline-flex items-center gap-2 ${props.className ?? ''}`}>
      <label htmlFor="pc-footer-language-picker" className="sr-only">
        {labelText}
      </label>
      <select
        id="pc-footer-language-picker"
        value={value}
        onChange={handleChange}
        disabled={busy}
        aria-label={labelText}
        className="bg-transparent border border-rule rounded-sm px-2 py-1 text-xs text-muted hover:text-ink focus:text-ink focus:outline-none focus:ring-1 focus:ring-ink"
      >
        {(SUPPORTED_LOCALES as readonly SupportedLocale[]).map((loc) => {
          if (!enabledSet.has(loc)) return null;
          return (
            <option key={loc} value={loc}>
              {LOCALE_LABELS[loc]}
            </option>
          );
        })}
      </select>
    </span>
  );
}
